import * as THREE from 'three';
import { SceneObject } from './sceneObject';
import z from 'zod';

export const FloorSchema = SceneObject.schema.extend({
  size: z.number().default(64),
  textureRepeat: z.number().default(8),
});

export class Floor extends SceneObject<typeof FloorSchema> {
  static override schema = FloorSchema;

  private readonly texture: THREE.CanvasTexture;
  private readonly mesh: THREE.Mesh;

  constructor(options: z.input<typeof FloorSchema> = {}) {
    super(options);

    this.texture = this.createTexture(this.data.textureRepeat);
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(this.data.size, this.data.size),
      new THREE.MeshStandardMaterial({
        map: this.texture,
        roughness: 1,
        metalness: 0,
      }),
    );
    this.quaternion.setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  }

  protected renderObject(parent: THREE.Object3D) {
    parent.add(this.mesh);
  }

  override dispose() {
    super.dispose();
    this.texture.dispose();
    this.mesh.geometry.dispose();

    if (Array.isArray(this.mesh.material)) {
      this.mesh.material.forEach(material => material.dispose());
    } else {
      this.mesh.material.dispose();
    }
  }

  private createTexture(repeat: number) {
    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      throw new Error('Unable to create floor texture context');
    }

    context.imageSmoothingEnabled = false;
    context.fillStyle = '#8b6f47';
    context.fillRect(0, 0, size, size);

    const cells = 8;
    const cellSize = size / cells;

    for (let row = 0; row < cells; row += 1) {
      for (let column = 0; column < cells; column += 1) {
        const isLight = (row + column) % 2 === 0;
        context.fillStyle = isLight ? '#c7a26a' : '#6f5533';
        context.fillRect(column * cellSize, row * cellSize, cellSize, cellSize);
      }
    }

    context.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    context.lineWidth = 4;
    for (let step = 0; step <= cells; step += 1) {
      const offset = step * cellSize;
      context.beginPath();
      context.moveTo(offset, 0);
      context.lineTo(offset, size);
      context.stroke();

      context.beginPath();
      context.moveTo(0, offset);
      context.lineTo(size, offset);
      context.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(repeat, repeat);

    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.anisotropy = 1;

    return texture;
  }
}
